import { Box, Button, useMultiStyleConfig } from "@chakra-ui/react";
import { keepPreviousData, useQueryClient } from "@tanstack/react-query";
import {
  getAdminCustomerEventImpactingEventsTableDataRetrieveQueryKey,
  getAdminCustomerEventTransactionTableDataRetrieveQueryKey,
  getCustomerEventRetrieveQueryKey,
  useAdminCustomerEventImpactingEventsOptionsRetrieve,
  useAdminCustomerEventImpactingEventsTableDataRetrieve,
  useAdminCustomerEventTransactionTableDataRetrieve,
  useCustomerEventRetrieveSuspense,
} from "gen/endpoints/customer-event/customer-event";
import { getCustomerEventAdminTableDataRetrieveQueryKey } from "gen/endpoints/customer-event-admin/customer-event-admin";
import {
  getFundFamilyEventRetrieveQueryKey,
  useFundFamilyEventDelete,
  useFundFamilyEventPendingToCompleteUpdate,
  useFundFamilyEventRetrieve,
} from "gen/endpoints/fund-family-event/fund-family-event";
import { compact, isEmpty } from "lodash-es";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { OutOfDateAlert } from "shared/components/alert/OutOfDateAlert";
import { SelectButton } from "shared/components/input/select/SelectButton";
import { selectOptionsFromGenericSelectData } from "shared/components/input/select/SelectUtils";
import { PageTabBody } from "shared/components/layout/PageTabBody";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { GetLinkInfoForRowFn } from "shared/components/table/data/DataTableTypes";
import { EqualizedViewToggleButton } from "shared/components/toggle/EqualizedViewToggleButton";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";
import { StringArrayParam } from "shared/utils/QueryParamUtils";
import { invalidateQueryKeys } from "shared/utils/QueryUtils";
import { useQueryParams } from "use-query-params";

export type FundFamilyEventDeleteArgs = Parameters<
  ReturnType<typeof useFundFamilyEventDelete>["mutateAsync"]
>[0];

export type FundFamilyEventFinalizeArgs = Parameters<
  ReturnType<typeof useFundFamilyEventPendingToCompleteUpdate>["mutateAsync"]
>[0];

export const EventDetailsTab: React.FC = () => {
  const { t } = useTranslation(["common", "admin", "capitalActivity", "enums"]);
  const navigate = useNavigate();
  const styles = useMultiStyleConfig("PageHeader", { size: "lg" });
  const queryClient = useQueryClient();
  const { customerEventId } = useRequiredStringParams();

  const [isEqualizedView, setIsEqualizedView] = useState<boolean>(false);

  const { data: customerEvent } =
    useCustomerEventRetrieveSuspense(customerEventId);

  const { data: fundFamilyEvent } = useFundFamilyEventRetrieve(
    customerEvent.fund_family_id!,
    customerEvent.fund_family_event_id!,
    {
      query: {
        enabled:
          !!customerEvent.fund_family_id &&
          !!customerEvent.fund_family_event_id,
      },
    },
  );

  const { data: impactingEventOptionsData } =
    useAdminCustomerEventImpactingEventsOptionsRetrieve(customerEventId, {
      query: {
        enabled: !!customerEvent.fund_family_event_id,
      },
    });

  const [filters, setFilters] = useQueryParams({
    fund_family_event_ids: StringArrayParam,
  });

  const hasFilters = Object.values(filters).some((value) => !isEmpty(value));
  const defaultFundFamilyEventId = compact([
    customerEvent.fund_family_event_id,
  ]);

  const clearFilters = () =>
    setFilters({
      fund_family_event_ids: defaultFundFamilyEventId,
    });

  const setIsEqualizedViewOnChange = (isEqualizedView: boolean) => {
    setIsEqualizedView(isEqualizedView);
    const impactingFundFamilyEventIds = impactingEventOptionsData?.map(
      (eventOptions) => eventOptions.value,
    );
    setFilters({
      fund_family_event_ids:
        isEqualizedView && !isEmpty(impactingFundFamilyEventIds)
          ? impactingFundFamilyEventIds
          : defaultFundFamilyEventId,
    });
  };

  const { data: impactingEventTableData } =
    useAdminCustomerEventImpactingEventsTableDataRetrieve(
      customerEventId,
      {
        is_equalized_view: isEqualizedView,
      },
      {
        query: {
          enabled: !!customerEvent.fund_family_event_id,
          placeholderData: keepPreviousData,
        },
      },
    );

  const { data: transactionTableData } =
    useAdminCustomerEventTransactionTableDataRetrieve(
      customerEventId,
      {
        posted_fund_family_event_ids:
          filters.fund_family_event_ids || defaultFundFamilyEventId,
      },
      {
        query: {
          enabled: !!customerEvent.fund_family_event_id,
          placeholderData: keepPreviousData,
        },
      },
    );

  const getLinkInfoForEventRow = useCallback<GetLinkInfoForRowFn>(({ row }) => {
    const metadata = row.data?.metadata;
    if (metadata?.type === "id_row") {
      return {
        type: "generic",
        to: rootRoute({}).admin({}).events({}).event({
          customerEventId: metadata.id,
        }).$,
      };
    }
    return undefined;
  }, []);

  const invalidateOnRefresh = useCallback(
    () =>
      invalidateQueryKeys(
        queryClient,
        getCustomerEventRetrieveQueryKey(customerEventId),
        getFundFamilyEventRetrieveQueryKey(
          customerEvent.fund_family_id!,
          customerEvent.fund_family_event_id!,
        ),
        getCustomerEventAdminTableDataRetrieveQueryKey(),
        getAdminCustomerEventImpactingEventsTableDataRetrieveQueryKey(
          customerEventId,
        ),
        getAdminCustomerEventTransactionTableDataRetrieveQueryKey(
          customerEventId,
        ),
      ),
    [customerEventId, customerEvent, queryClient],
  );

  const eventsTablePath = rootRoute({}).admin({}).events({}).$;

  return (
    <PageTabBody>
      {fundFamilyEvent && !!fundFamilyEvent.out_of_date_reason && (
        <OutOfDateAlert
          fundFamilyId={fundFamilyEvent.fund_family_id}
          fundFamilyEventId={fundFamilyEvent.id!}
          message={fundFamilyEvent.out_of_date_reason}
          onDiscard={() => navigate(eventsTablePath)}
          onRefresh={async () => await invalidateOnRefresh()}
        />
      )}
      <Box
        className="mb-page-header__actions"
        sx={styles.actions}
        justifyContent="flex-end"
        marginBottom={4}
      >
        <EqualizedViewToggleButton
          isEqualizedView={isEqualizedView}
          setIsEqualizedView={setIsEqualizedViewOnChange}
          size="lg"
        />
      </Box>
      {!!impactingEventTableData?.rows.length && (
        <TableBox>
          <TableBoxHeader>
            <TableBoxTitle>{t("common:events")}</TableBoxTitle>
            <DataTableBoxActions />
          </TableBoxHeader>
          <TableBoxBody>
            <DataTable
              data={impactingEventTableData}
              getLinkInfoForRow={getLinkInfoForEventRow}
            />
          </TableBoxBody>
        </TableBox>
      )}
      {!!transactionTableData?.rows.length && (
        <TableBox>
          <TableBoxHeader>
            <TableBoxTitle>{t("common:transactions")}</TableBoxTitle>
            <DataTableBoxActions>
              {impactingEventOptionsData?.length && (
                <SelectButton
                  isMulti={true}
                  title={t("common:events")}
                  size="sm"
                  isFilterButton={true}
                  value={
                    filters.fund_family_event_ids || defaultFundFamilyEventId
                  }
                  onChangeValue={(fund_family_event_ids) =>
                    setFilters({
                      fund_family_event_ids: isEmpty(fund_family_event_ids)
                        ? defaultFundFamilyEventId
                        : fund_family_event_ids,
                    })
                  }
                >
                  {selectOptionsFromGenericSelectData(
                    impactingEventOptionsData,
                  )}
                </SelectButton>
              )}
              {hasFilters && (
                <Button variant="subtle" onClick={clearFilters}>
                  {t("common:reset")}
                </Button>
              )}
            </DataTableBoxActions>
          </TableBoxHeader>
          <TableBoxBody>
            <DataTable data={transactionTableData} />
          </TableBoxBody>
        </TableBox>
      )}
    </PageTabBody>
  );
};
